import styled from 'styled-components';
import React, { FC, FormEventHandler, useEffect, useState } from 'react';
import theme from '../utils/theme';
import PrimaryButton, { SecondaryButton } from './Button';

export const PricePreviewBlockedApiKeysForm: FC<{ authToken: string }> = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const authHeaders = { Authorization: 'Bearer ' + props.authToken };

  const fetchEntries = () => {
    fetch(`${process.env.PRIVATE_API_URL}/price-preview-blocked-api-keys`, {
      headers: authHeaders,
      mode: 'cors',
    }).then((response) => {
      if (response.status !== 200) {
        response.text().then((body) => setError(body.trim().length > 0 ? body : 'Failed to fetch entries'));
      } else {
        response.json().then((data: string[]) => setApiKeys(data));
      }
    });
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleFormSubmission: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    const payload = new URLSearchParams(new FormData(form) as any);

    setIsLoading(true);
    setError(null);
    setResult(null);

    fetch(form.action, {
      method: 'post',
      body: payload,
      headers: authHeaders,
    })
      .then((response) => {
        setIsLoading(false);
        if (response.status !== 200) {
          response.text().then((body) => setError(body.trim().length > 0 ? body : 'Failed to block API key'));
        } else {
          setResult('API key blocked successfully');
          response.json().then((data: string[]) => setApiKeys(data));
          form.reset();
        }
      })
      .catch((e) => {
        setIsLoading(false);
        setError(e instanceof Error ? e.message : e.toString());
      });
  };

  const handleDelete = (apiKey: string) => {
    if (!confirm(`Unblock API key "${apiKey}"?`)) return;

    setDeletingKey(apiKey);
    setError(null);
    setResult(null);

    fetch(`${process.env.PRIVATE_API_URL}/price-preview-blocked-api-keys/${encodeURIComponent(apiKey)}`, {
      method: 'delete',
      headers: authHeaders,
      mode: 'cors',
    })
      .then((response) => {
        setDeletingKey(null);
        if (response.status !== 200) {
          response.text().then((body) => setError(body.trim().length > 0 ? body : 'Failed to unblock API key'));
        } else {
          setResult('API key unblocked successfully');
          response.json().then((data: string[]) => setApiKeys(data));
        }
      })
      .catch((e) => {
        setDeletingKey(null);
        setError(e instanceof Error ? e.message : e.toString());
      });
  };

  return (
    <>
      <h3>Price Preview Blocked API Keys</h3>

      <form method="POST" action={`${process.env.PRIVATE_API_URL}/price-preview-blocked-api-keys`} onSubmit={handleFormSubmission}>
        <p>
          <Label htmlFor="api-key">API Key:</Label>
          <Input type="text" name="api-key" required={true} placeholder="e.g. f1f1d25e601e4cafb9c8ec0b5cc743db" />
        </p>
        <PrimaryButton disabled={isLoading} isLoading={isLoading} type="submit">
          Block API Key
        </PrimaryButton>

        {result !== null && <p style={{ color: theme.colours.confirmation400 }}>{result}</p>}
        {error !== null && <p style={{ color: theme.colours.alert400 }}>{error}</p>}
      </form>

      {apiKeys.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>API Key</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((apiKey) => (
              <tr key={apiKey}>
                <Td>{apiKey}</Td>
                <Td>
                  <SecondaryButton
                    disabled={deletingKey === apiKey}
                    isLoading={deletingKey === apiKey}
                    onClick={() => handleDelete(apiKey)}
                  >
                    Unblock
                  </SecondaryButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
};

const Label = styled.label`
  font-size: 14px;
  display: block;
  margin-bottom: 5px;
  font-weight: ${theme.fontWeightMedium};
  min-width: 200px;
`;

const Input = styled.input`
  height: 44px;
  color: ${theme.colours.black};
  top: 25px;
  left: 12px;
  line-height: 20px;
  padding-left: 12px;
  width: 350px;
`;

const Table = styled.table`
  margin-top: 20px;
  border-collapse: collapse;
  width: 100%;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${theme.colours.gray300};
  font-size: 14px;
  font-weight: ${theme.fontWeightMedium};
`;

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid ${theme.colours.gray300};
  font-size: 14px;
`;
